'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslations } from 'next-intl';
import { useReadContract } from 'wagmi';

import { poolContractAbi } from '@/abi';
import { PoolsAfterSaleTab } from '@/app/[locale]/(main)/(home)/_components/Pools/PoolsAfterSaleTab';
import { TokenInfoEnded } from '@/app/[locale]/(main)/(home)/_components/Pools/TokenInfoEnded';
import { CountdownTimer } from '@/components/shared/Countdown';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils/cn';
import { useCountdownRenderer } from '@/lib/utils/getCountdownRenderer';
import { getPresaleEndMs } from '@/lib/utils/getPresaleEndMs';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';
import { T_Pool } from '@/ts/types/T_Pool';

import { TokenInfo } from './TokenInfo';
import { TokenPurchase } from './TokenPurchase';
import { TokenTabs } from './TokenTabs';

import type { Address } from 'viem';

interface I_PoolsContentProps {
  tokens: T_Pool[];
  className?: string;
  closeModal?: () => void;
  hideLearnMore?: boolean;
  isModal?: boolean;
}

const getTokenId = (token: T_Pool) => token?.uuid;

export function PoolsContent({
  tokens: initialTokens,
  className = '',
  closeModal,
  hideLearnMore = false,
  isModal = false
}: I_PoolsContentProps) {
  const [tokens, setTokens] = useState<T_Pool[]>(initialTokens);
  const [selectedTokenId, setSelectedTokenId] = useState(
    () => getTokenId(initialTokens?.[0]) || ''
  );
  const [selectedToken, setSelectedToken] = useState<T_Pool | null>(() => {
    return (
      initialTokens.find(
        token => getTokenId(token) === getTokenId(initialTokens?.[0])
      ) ||
      initialTokens[0] ||
      null
    );
  });
  const [updateCounter, setUpdateCounter] = useState(0);
  const t = useTranslations('pools');
  const countdownRenderer = useCountdownRenderer(t);

  // Read feePercent from the pool contract
  const poolContractAddress = selectedToken?.poolAddress as Address | undefined;
  const { data: feePercent } = useReadContract({
    address: poolContractAddress,
    abi: poolContractAbi,
    functionName: 'feePercent',
    query: { enabled: !!poolContractAddress }
  }) as { data: bigint | undefined };

  // Convert feePercent from contract (likely in basis points, e.g., 100 = 1%, 1000 = 10%)
  // Assuming feePercent is stored as basis points (10000 = 100%)
  const feePercentValue = useMemo(() => {
    if (!feePercent) return 0;
    // Convert from basis points to percentage (e.g., 100 = 1%, 1000 = 10%)
    // If stored as basis points: feePercent / 100 = percentage
    // If stored as percentage: feePercent = percentage
    // Common pattern: feePercent is in basis points where 10000 = 100%
    // So we divide by 100 to get percentage (e.g., 100 = 1%)
    return Number(feePercent) / 100;
  }, [feePercent]);

  const selectToken = useCallback((token: T_Pool | null) => {
    if (token) {
      const tokenId = getTokenId(token);
      setSelectedTokenId(tokenId);
      setSelectedToken(token);
    }
  }, []);

  useEffect(() => {
    setTokens(initialTokens);
    if (selectedTokenId) {
      const newSelectedToken = initialTokens.find(
        token => getTokenId(token) === selectedTokenId
      );
      if (newSelectedToken) {
        selectToken(newSelectedToken);
      } else if (initialTokens.length > 0) {
        selectToken(initialTokens[0]);
      }
    }
  }, [initialTokens, selectedTokenId, selectToken]);

  const handleTokenChange = (tokenId: string) => {
    const token = tokens.find(token => getTokenId(token) === tokenId);
    if (token) {
      selectToken(token);
    }
  };

  const handleAmountChange = (_amount: number) => {};

  const handlePoolDataUpdate = (boughtTokenAmount: number) => {
    if (
      boughtTokenAmount > 0 &&
      isFinite(boughtTokenAmount) &&
      !isNaN(boughtTokenAmount)
    ) {
      setSelectedToken(prevSelectedToken => {
        if (!prevSelectedToken?.blockchainData) {
          return prevSelectedToken;
        }

        // Calculate the actual tokens after removing fee percent
        // Formula: actualTokens = boughtTokenAmount * (1 - feePercent / 100)
        const feeAdjustedAmount =
          feePercentValue > 0
            ? boughtTokenAmount * (1 - feePercentValue / 100)
            : boughtTokenAmount;

        const currentTotalSoldStr =
          prevSelectedToken.blockchainData.totalSoldTokensDecimal || '0';
        const currentTotalSold = parseFloat(currentTotalSoldStr) || 0;
        const newTotalSold = currentTotalSold + feeAdjustedAmount;

        if (
          isFinite(newTotalSold) &&
          !isNaN(newTotalSold) &&
          newTotalSold >= 0
        ) {
          const updatedBlockchainData = {
            ...prevSelectedToken.blockchainData,
            totalSoldTokensDecimal: newTotalSold.toString()
          };

          const updatedToken: T_Pool = {
            ...prevSelectedToken,
            blockchainData: updatedBlockchainData
          };

          setTokens(prevTokens =>
            prevTokens.map(token => {
              if (getTokenId(token) === selectedTokenId) {
                return updatedToken;
              }
              return token;
            })
          );

          setUpdateCounter(prev => prev + 1);

          return updatedToken;
        }

        return prevSelectedToken;
      });
    }
  };

  if (!selectedToken) {
    return null;
  }

  const presaleEndMs = getPresaleEndMs(selectedToken.presaleEndDate);
  const isSelectedTokenPresaleEnded =
    presaleEndMs !== null && Date.now() >= presaleEndMs;

  const isAllPresalesEnded = tokens.every(token => {
    const ms = getPresaleEndMs(token.presaleEndDate);
    return ms !== null && Date.now() >= ms;
  });

  return (
    <div className={cn('w-full rounded-2xl h-full', className)}>
      <Tabs
        value={selectedTokenId}
        onValueChange={handleTokenChange}
        className="w-full gap-0 rounded-2xl h-full"
      >
        <TokenTabs tokens={tokens} />

        <TabsContent
          value={selectedTokenId}
          className={cn('mt-0 min-h-110 h-full', 'md:overflow-visible')}
        >
          {isAllPresalesEnded ? (
            <PoolsAfterSaleTab
              token={selectedToken}
              isModal={isModal}
              closeModal={closeModal}
            />
          ) : isSelectedTokenPresaleEnded ? (
            <TokenInfoEnded
              token={selectedToken}
              isModal={isModal}
              closeModal={closeModal}
            />
          ) : (
            <>
              <TokenInfo
                key={`${selectedTokenId}-${selectedToken?.blockchainData?.totalSoldTokensDecimal || '0'}-${updateCounter}`}
                token={selectedToken}
              />
              <TokenPurchase
                closeModal={closeModal}
                selectedToken={selectedToken}
                onAmountChange={handleAmountChange}
                hideLearnMore={hideLearnMore}
                onPoolDataUpdate={handlePoolDataUpdate}
              />
              <div
                className={cn(
                  'flex flex-col gap-5',
                  isModal
                    ? 'min-h-40 pb-10 flex-col-reverse px-7 justify-end'
                    : ''
                )}
              >
                <div className={cn('flex px-7 ', isModal ? 'px-0 pb-10' : '')}>
                  <div
                    className={cn(
                      'w-full text-center text-sm font-semibold bg-accordion-brand gap-2 py-1 px-2.5 rounded-full',
                      isModal ? 'flex-row gap-1' : ''
                    )}
                  >
                    {t.rich('bySubmittingAgree', {
                      termsOfUse: (children: React.ReactNode) => (
                        <Link
                          href={C_Routes[E_Routes.TERMS_AND_CONDITIONS].link}
                          className="ml-1 text-sm font-medium underline leading-tight"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {children}
                        </Link>
                      )
                    })}
                  </div>
                </div>
                {presaleEndMs !== null && (
                  <div
                    className={cn(
                      'p-3 bg-countdown-bg rounded-bl-xl rounded-br-xl',
                      isModal ? 'min-h-10 rounded-2xl' : ''
                    )}
                  >
                    <CountdownTimer
                      date={presaleEndMs}
                      renderer={countdownRenderer}
                      className="flex justify-center text-center"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
